import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ROLE_LABELS } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { LANGUAGE_NAMES, LANGUAGES, type Language } from '@/i18n/languages';
import { useTenantSettings } from '@/tenant/useTenantSettings';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Marca } from '@/ui/Marca';

/**
 * Cabecera: qué marca, quién sos, con qué rol, en qué idioma, y salir.
 *
 * El rol se muestra **siempre**. En un sistema con tres roles jerárquicos,
 * "por qué no me aparece el botón de retirar el lote" es la pregunta número
 * uno de soporte, y tenerla contestada en pantalla la elimina.
 */
export function TopBar({
  onToggleNav,
  navOpen,
}: {
  onToggleNav: () => void;
  navOpen: boolean;
}): ReactNode {
  const { t, language, setLanguage } = useI18n();
  const { claims, logout } = useAuth();
  const tenant = useTenantSettings();
  const [leaving, setLeaving] = useState(false);

  const handleLogout = (): void => {
    setLeaving(true);
    void logout().finally(() => {
      setLeaving(false);
    });
  };

  return (
    <header className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2.5">
      {/* Hamburguesa, no un botón con texto: es el signo que cualquier
          persona reconoce como "menú" en un teléfono, y el texto "Abrir
          navegación" se comía un tercio de la barra. El nombre accesible sigue
          siendo el texto, para el lector de pantalla. */}
      <Button
        variant="ghost"
        size="sm"
        className="-ml-1 px-2 lg:hidden"
        onClick={onToggleNav}
        aria-expanded={navOpen}
        aria-controls="app-nav"
        aria-label={navOpen ? t('nav.close') : t('nav.open')}
      >
        <MenuIcon open={navOpen} />
      </Button>

      <div className="min-w-0">
        {/* El nombre de la marca es lo que identifica la pantalla —el dashboard
            es marca blanca—, así que se lleva la tipografía de titular. La
            firma de XYVONA queda debajo, en gris y en la de interfaz. */}
        <p className="font-display truncate text-sm font-bold tracking-tight text-ink">
          {tenant.name}
        </p>
        {/* La firma de XYVONA va subordinada al nombre del cliente y con el
            símbolo a un solo color: el panel es marca blanca, y acá adentro el
            degradado competiría con los colores de estado. */}
        <p className="hidden items-center gap-1.5 truncate text-xs text-ink-subtle sm:flex">
          <Marca variante="simbolo" alto={16} plana />
          {t('app.name')} · {t('app.subtitle')}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        <label className="flex items-center gap-1.5 text-xs text-ink-subtle">
          <span className="sr-only md:not-sr-only">{t('nav.language')}</span>
          <select
            value={language}
            onChange={(event) => {
              setLanguage(event.target.value as Language);
            }}
            className="rounded border border-border bg-surface px-2 py-1 text-xs text-ink"
          >
            {LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {LANGUAGE_NAMES[code]}
              </option>
            ))}
          </select>
        </label>

        {claims !== null && (
          /* El nombre propio lleva a Mi cuenta (#192): es donde cualquier
             persona espera encontrar "cambiar mi contraseña", y la tabla de
             Usuarios no ofrece acciones sobre la fila propia a propósito.
             En pantalla angosta el nombre no entra y queda sólo la inicial en
             un círculo, que sigue siendo el enlace: si se ocultara entero,
             desde un teléfono no habría cómo llegar. */
          <Link
            to="/account"
            className="flex items-center gap-2 rounded hover:underline focus-visible:outline focus-visible:outline-2"
            aria-label={t('account.link')}
            title={t('account.link')}
          >
            <span
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar text-xs font-semibold text-sidebar-active-ink"
            >
              {initials(claims.name ?? claims.email)}
            </span>
            <span className="hidden text-right sm:block">
              <span className="block max-w-[14rem] truncate text-sm text-ink">
                {claims.name ?? claims.email}
              </span>
              <span className="block text-xs text-ink-subtle">
                <span className="sr-only">{t('auth.role.label')}: </span>
                <Badge tone="neutral">{ROLE_LABELS[claims.role]}</Badge>
              </span>
            </span>
          </Link>
        )}

        {/* En pantalla angosta el texto "Cerrar sesión" no entra junto al
            resto; queda el ícono con el mismo nombre accesible. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleLogout}
          disabled={leaving}
          aria-label={leaving ? t('auth.loggingOut') : t('auth.logout')}
          title={t('auth.logout')}
          className="px-2 sm:px-3"
        >
          <LogoutIcon />
          <span className="hidden sm:inline">
            {leaving ? t('auth.loggingOut') : t('auth.logout')}
          </span>
        </Button>
      </div>
    </header>
  );
}

/** "Edgar Páez" → "EP"; "admin.main@xyvona.com" → "A". Sólo para el círculo. */
function initials(label: string): string {
  const parts = label.split('@')[0]?.trim().split(/\s+/).filter(Boolean) ?? [];
  const letters = parts.slice(0, 2).map((part) => part[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

/** Tres líneas o una cruz, según el estado del cajón. `aria-hidden`: el
 *  nombre lo da el botón. */
function MenuIcon({ open }: { open: boolean }): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {open ? (
        <>
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </>
      ) : (
        <>
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </>
      )}
    </svg>
  );
}

function LogoutIcon(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
      <path d="M21 4v16" />
    </svg>
  );
}
